import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Download, ExternalLink, FastForward, Info, SkipForward, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { copyText, fmtDate, imageUrl } from "@/lib/api";
import { openGroupWindow } from "@/lib/popup";
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";

export default function FastPostCard({ item, notes, needsApproval, busy, onDone, onSkip, usePopup = true, opened = false, onDoneNext, helper = false, onOpen, mobile = false }) {
  const [text, setText] = useState(item.text);
  const [awaiting, setAwaiting] = useState(false);
  const [ask, setAsk] = useState(false);

  useEffect(() => { setText(item.text); setAwaiting(opened); setAsk(false); }, [item.tid, item.text, opened]);

  // Phone: when the user comes back from the Facebook app, ask "Sudah post?"
  useEffect(() => {
    if (!mobile || !awaiting) return undefined;
    const onVis = () => { if (document.visibilityState === "visible") setAsk(true); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [mobile, awaiting]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "TEXTAREA" || busy) return;
      if (e.key === "Enter") { e.preventDefault(); copyOpen(); }
      if (e.key.toLowerCase() === "d") onDone();
      if (e.key.toLowerCase() === "n" && onDoneNext) onDoneNext();
      if (e.key.toLowerCase() === "s") onSkip();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const copyOpen = async () => {
    if (helper && onOpen) { onOpen(text); setAwaiting(true); return; }
    // start copy while this window still has focus, then open (inside the user gesture)
    const copying = copyText(text);
    const mode = openGroupWindow(item.group_url, usePopup);
    await copying;
    if (mode === "blocked") {
      toast.error("Popup disekat oleh pelayar. Benarkan popup untuk laman ini, atau matikan suis 'Buka dalam popup'.");
      window.open(item.group_url, "_blank", "noopener,noreferrer");
    } else {
      toast.success(mode === "popup" ? "Teks disalin. Tampal (Ctrl+V) dalam popup group, post, tutup popup (Ctrl+W), kemudian tekan Sudah Post." : "Teks disalin. Tampal (paste) dalam group, kemudian kembali ke sini.");
    }
    setAwaiting(true);
  };

  return (
    <div className="panel fade-up p-6 sm:p-8" data-testid="fast-post-card">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15 text-primary"><Users className="h-5 w-5" /></div>
        <div className="min-w-0">
          <p className="truncate text-xl font-bold" data-testid="fast-post-group-name">{item.group_name}</p>
          <p className="font-mono text-[11px] text-muted-foreground">Dijadualkan {fmtDate(item.scheduled_at)}</p>
        </div>
      </div>
      {notes && (
        <p className="mt-5 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-200" data-testid="fast-post-group-notes">
          <Info className="mt-0.5 h-4 w-4 shrink-0" /> {notes}
        </p>
      )}
      {needsApproval && (
        <p className="mt-5 rounded-lg border border-violet-500/30 bg-violet-500/10 px-3 py-2 text-sm text-violet-200" data-testid="fast-post-needs-approval">
          Group ini perlu kelulusan admin — selepas “Sudah Post”, ia akan masuk tab <b>Menunggu Admin</b>.
        </p>
      )}
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} className="mt-5 bg-background text-[15px] leading-relaxed" data-testid="fast-post-text" />
      {item.image_id && (
        <div className="mt-4 flex items-center gap-3">
          <img src={imageUrl(item.image_id)} alt="Lampiran" className="h-16 w-16 rounded-md border border-border object-cover" />
          <a href={imageUrl(item.image_id)} download className="flex items-center gap-1 text-sm text-primary hover:underline" data-testid="fast-post-download-image">
            <Download className="h-4 w-4" /> Muat turun gambar untuk dilampirkan
          </a>
        </div>
      )}
      <div className="mt-6 grid gap-3 sm:grid-cols-[1.4fr_1fr_auto]">
        <Button onClick={copyOpen} disabled={busy} className="h-12 text-base font-semibold" data-testid="fast-post-copy-open-btn">
          <ExternalLink className="mr-2 h-4 w-4" /> 1. {helper ? "Buka & Isi Cipta Siaran" : "Salin & Buka Group"}
        </Button>
        <Button onClick={onDone} disabled={busy} className={`h-12 bg-emerald-600 text-base font-semibold hover:bg-emerald-500 ${awaiting ? "animate-pulse ring-2 ring-emerald-400/70" : ""}`} data-testid="fast-post-done-btn">
          <CheckCircle2 className="mr-2 h-4 w-4" /> 2. Sudah Post
        </Button>
        <Button variant="ghost" onClick={onSkip} disabled={busy} className="h-12 text-muted-foreground" data-testid="fast-post-skip-btn">
          <SkipForward className="mr-2 h-4 w-4" /> Langkau
        </Button>
      </div>
      {onDoneNext && (
        <Button onClick={onDoneNext} disabled={busy} variant="secondary" className="mt-3 h-12 w-full border border-emerald-500/40 text-base font-semibold text-emerald-200 hover:bg-emerald-500/15" data-testid="fast-post-done-next-btn">
          <FastForward className="mr-2 h-4 w-4" /> Sudah Post & Buka Group Seterusnya
        </Button>
      )}
      <p className="mt-4 hidden font-mono text-[11px] text-muted-foreground sm:block">Pintasan: Enter = salin & buka · D = sudah post · {onDoneNext ? "N = sudah post & seterusnya · " : ""}S = langkau</p>
      {mobile && (
        <Drawer open={ask} onOpenChange={setAsk}>
          <DrawerContent className="border-border bg-card" data-testid="fast-post-mobile-ask">
            <DrawerHeader>
              <DrawerTitle>Sudah post ke group ini?</DrawerTitle>
              <DrawerDescription className="truncate">{item.group_name}</DrawerDescription>
            </DrawerHeader>
            <div className="grid gap-2 px-4 pb-6" style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}>
              <Button onClick={() => { setAsk(false); onDone(); }} disabled={busy} className="h-14 bg-emerald-600 text-base font-semibold text-white hover:bg-emerald-500" data-testid="fast-post-mobile-done">
                <CheckCircle2 className="mr-2 h-5 w-5" /> Ya, sudah post
              </Button>
              <Button variant="secondary" onClick={() => { setAsk(false); copyOpen(); }} className="h-12" data-testid="fast-post-mobile-reopen">
                <ExternalLink className="mr-2 h-4 w-4" /> Belum - salin & buka semula
              </Button>
              <Button variant="ghost" onClick={() => { setAsk(false); onSkip(); }} disabled={busy} className="h-12 text-muted-foreground" data-testid="fast-post-mobile-skip">
                <SkipForward className="mr-2 h-4 w-4" /> Langkau group ini
              </Button>
            </div>
          </DrawerContent>
        </Drawer>
      )}
    </div>
  );
}
